import { useState } from 'react';
import styled from '@emotion/styled';
import { mediaQuery, Spacings, Fonts } from '@styles/index';
import Image from '@base/Image';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import { segment } from '@actions/analytics';
import H3 from '@base/H3/H3';
import H5 from '@base/H5/H5';
import Button from 'aquilla/core/molecules/Button';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import loadStyle from '@utils/loadStyle';
import { FRESHBOT_STYLE } from '@utils/constants';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import RewardDetail from './RewardDetails';
import {
  CHAT_WITH_US_CLICKED,
  REFER_FAQ_CLICKED,
  REFER_TERMS_CLICKED,
  REFERRAL_REWARD_KNOW_MORE
} from '../constants';

const Container = styled.div`
  gap: ${Spacings.SPACING_8B};
  margin-top: ${Spacings.SPACING_20B};
  padding-bottom: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    flex-direction: column;
    margin-top: ${Spacings.SPACING_8B};
    gap: ${Spacings.SPACING_6B};
    position: relative;
    top: unset;
    z-index: unset;
  }
`;

const RightRow = styled.div`
  margin-left: ${Spacings.SPACING_35B};
  width: 368px;
  display: flex;
  flex: 1;
  flex-direction: column;
  cursor: pointer;
  border: 1px solid var(--color-divider-secondary);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_4B} ${Spacings.SPACING_1B}
    ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    margin-left: ${Spacings.SPACING_0B};
    width: 100%;
  }
`;

const ReferralHeading = styled(H3)`
  margin-left: ${Spacings.SPACING_35B};
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H4};
    margin-left: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const AboutPoint = styled.div`
  display: flex;
  align-items: center;
  padding-top: ${Spacings.SPACING_3B};
  padding-bottom: ${Spacings.SPACING_3B};
  gap: ${Spacings.SPACING_4B};
`;

const PointText = styled(H5)`
  flex-grow: 1;
`;

const DoubleRewardContainer = styled.div`
  display: flex;
  box-sizing: border-box;
  font-style: normal;
  align-items: flex-start;
  flex-direction: row;
  width: 366px;
  margin-left: ${Spacings.SPACING_35B};
  border: 1px solid var(--color-divider-secondary);
  border-radius: ${Spacings.SPACING_2B};
  flex: none;
  order: 1;
  flex-grow: ${Spacings.SPACING_0B};
  ${mediaQuery.uptoTablet} {
    margin-left: ${Spacings.SPACING_0B};
    width: 100%;
    margin-bottom: ${Spacings.SPACING_10B};
  }
`;

const DoubleReward = styled.div`
  display: flex;
  padding: ${Spacings.SPACING_4B};
  flex-grow: 1;
  gap: ${Spacings.SPACING_4B};
`;

const DoubleRewardText = styled.div``;

const Title = styled(H5)`
  color: var(--color-text-primary);
`;

const AboutReferralWrapper = styled.div`
  position: sticky;
  top: 104px;
  z-index: 1;
  margin-bottom: ${Spacings.SPACING_20B};
`;

const SubTitle = styled.div`
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.H7};
  margin-top: 6px;
  line-height: ${Spacings.SPACING_4B};
  color: var(--color-i-gray-13);
`;

const ImageWrapper = styled.div`
  margin-top: 0%;
`;

const GetSubscription = styled(Button)`
  margin-top: ${Spacings.SPACING_3B};
  padding: ${Spacings.SPACING_0B};
  height: ${Spacings.SPACING_8B};
  width: 133px;
  font-size: ${Fonts.H7};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Spacings.SPACING_4B};
  border-radius: 6px;
`;

const AboutReferral = ({
  isPaid,
  rewardDetails,
  userType,
  referralBenefits = {},
  setReferralClicked = () => {},
  routeChangeStart
}) => {
  const router = useRouter();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const selectedGoalUID = useSelectedGoalUID();
  const goalSegmentData = useGoalEventProps({ goalUID: selectedGoalUID });
  const [drawerState, setDrawerState] = useState(false);
  const closeRewardDetailDrawer = () => {
    setDrawerState(false);
  };
  const handleKnowMore = () => {
    setDrawerState(true);
    segment.track(REFERRAL_REWARD_KNOW_MORE, {
      ...goalSegmentData,
      referral_type: userType
    });
  };
  const handleOpenChat = () => {
    loadStyle(FRESHBOT_STYLE).then(() => {
      if (!window.Freshbots) return;
      if (!window.Freshbots.freddyIsInitiatedChat) {
        window.Freshbots.initiateChat(true);
        window.Freshbots.freddyIsInitiatedChat = true;
      }
      window.Freshbots.showWidget(true);
    });
    segment.track(CHAT_WITH_US_CLICKED, {
      ...goalSegmentData,
      referral_type: userType
    });
  };

  const handleTermsClick = () => {
    setReferralClicked(true);
    router.events.off('routeChangeStart', routeChangeStart);
    router.push('/terms/referral-terms');
    segment.track(REFER_TERMS_CLICKED, goalSegmentData);
  };

  const handleFaqClick = () => {
    setReferralClicked(true);
    router.events.off('routeChangeStart', routeChangeStart);
    router.push('/helpcenter/category/81000193487/article/81000383213');
    segment.track(REFER_FAQ_CLICKED, goalSegmentData);
  };

  const getArrowIcon = () => (
    <RightChevron
      alt="navigate"
      width={Spacings.SPACING_6B}
      height={Spacings.SPACING_6B}
    />
  );

  const handleGetSubscription = () => {
    setReferralClicked(true);
    router.events.off('routeChangeStart', routeChangeStart);
    if (selectedGoalUID) {
      pushRoute('/goal/[goalSlug]/[goalUID]', `/goal/-/${selectedGoalUID}`);
    } else {
      pushRoute('/', '/');
    }
  };

  return (
    <AboutReferralWrapper>
      <Container>
        <ReferralHeading>More on rewards</ReferralHeading>
        <RightRow>
          <AboutPoint onClick={handleKnowMore}>
            <Image
              src="referrals/referralDetails.svg"
              width={32}
              height={32}
              alt="avatar"
            />
            <PointText>Reward details</PointText>
            {getArrowIcon()}
          </AboutPoint>
          <AboutPoint onClick={handleTermsClick}>
            <Image
              src="referrals/terms.svg"
              width={32}
              height={32}
              alt="avatar"
            />
            <PointText>Terms and conditions</PointText>
            {getArrowIcon()}
          </AboutPoint>
          <AboutPoint onClick={handleFaqClick}>
            <Image
              src="referrals/faq.svg"
              width={32}
              height={32}
              alt="avatar"
            />
            <PointText>Frequently asked questions</PointText>
            {getArrowIcon()}
          </AboutPoint>
          {isPaid && (
            <AboutPoint onClick={handleOpenChat}>
              <Image
                src="referrals/chat.svg"
                width={32}
                height={32}
                alt="avatar"
              />
              <PointText>Chat with us</PointText>
              {getArrowIcon()}
            </AboutPoint>
          )}
        </RightRow>
      </Container>
      <RewardDetail
        drawerData={rewardDetails}
        drawerState={drawerState}
        closeRewardDetailDrawer={closeRewardDetailDrawer}
      />
      {!isPaid && (
        <DoubleRewardContainer>
          <DoubleReward>
            <DoubleRewardText>
              <Title>
                {referralBenefits?.heading ??
                  'Subscribe to double your rewards'}
              </Title>
              <SubTitle>
                {referralBenefits?.sub_heading ??
                  'And a chance to win a branded laptop'}
              </SubTitle>
              {isLoggedIn && (
                <GetSubscription
                  variant="brand"
                  label="Get Subscription"
                  onClick={handleGetSubscription}
                />
              )}
            </DoubleRewardText>
            <ImageWrapper>
              <Image
                src={referralBenefits?.icon_url ?? 'referrals/bottomSheet.svg'}
                width={isLoggedIn ? 82 : 70}
                height={isLoggedIn ? 82 : 70}
                alt="avatar"
              />
            </ImageWrapper>
          </DoubleReward>
        </DoubleRewardContainer>
      )}
    </AboutReferralWrapper>
  );
};

export default AboutReferral;
